import { useEffect, useState, type CSSProperties } from "react";
import {
  Alert,
  Anchor,
  Badge,
  Box,
  Button,
  Container,
  Group,
  Loader,
  Paper,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import {
  IconArrowLeft,
  IconArrowRight,
  IconBook2,
  IconExternalLink,
  IconSearch,
  IconShieldCheck,
} from "@tabler/icons-react";
import { NextGenBrand } from "@evolys/ui";
import type {
  PublicEducationArticleDto,
  PublicEducationArticleSummaryDto,
  PublicMarketingSiteDto,
} from "@evolys/types";
import { RichArticleBody } from "./RichArticleBody";

type LoadState = "loading" | "ready" | "missing" | "error";

function articleUrl(slug: string) {
  return `/learn/${encodeURIComponent(slug)}`;
}

function EducationHeader({ site }: { site: PublicMarketingSiteDto }) {
  return (
    <header className="site-header education-header">
      <Container size="xl">
        <Group
          className="education-header-row"
          justify="space-between"
          py="md"
          wrap="nowrap"
        >
          <Anchor href="/" className="brand-link education-header-brand">
            <NextGenBrand
              name={site.laboratoryName}
              subtitle="Health education library"
              imageSize={44}
            />
          </Anchor>
          <Group className="education-header-actions" gap="xs" wrap="nowrap">
            <Button component="a" href="/learn" variant="subtle">
              <span className="education-action-long">Browse articles</span>
              <span className="education-action-short">Learn</span>
            </Button>
            <Button component="a" href="/" variant="light">
              <span className="education-action-long">Laboratory home</span>
              <span className="education-action-short">Home</span>
            </Button>
          </Group>
        </Group>
      </Container>
    </header>
  );
}

export function EducationIndex({
  site,
  style,
}: {
  site: PublicMarketingSiteDto;
  style: CSSProperties;
}) {
  const [articles, setArticles] = useState<PublicEducationArticleSummaryDto[]>([]);
  const [state, setState] = useState<LoadState>("loading");
  const [search, setSearch] = useState("");

  useEffect(() => {
    const controller = new AbortController();
    fetch("/api/public/articles", {
      cache: "no-store",
      credentials: "omit",
      headers: { Accept: "application/json" },
      signal: controller.signal,
    })
      .then(async (response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        setArticles(await response.json() as PublicEducationArticleSummaryDto[]);
        setState("ready");
      })
      .catch((error: unknown) => {
        if (error instanceof DOMException && error.name === "AbortError") return;
        setState("error");
      });
    return () => controller.abort();
  }, []);

  const visible = articles.filter((article) => {
    const term = search.trim().toLowerCase();
    return !term ||
      article.title.toLowerCase().includes(term) ||
      article.summary.toLowerCase().includes(term) ||
      article.category.toLowerCase().includes(term);
  });

  return (
    <Box className="site education-site" style={style}>
      <EducationHeader site={site} />
      <main>
        <section className="education-hero">
          <Container size="xl">
            <Text className="eyebrow">Clinically reviewed · cited · practical</Text>
            <Title order={1}>The diagnostic field guide</Title>
            <Text className="hero-body">
              Understand what laboratory tests measure, how patterns are
              interpreted, and which questions to discuss with your clinician.
            </Text>
            <TextInput
              className="education-search"
              size="lg"
              leftSection={<IconSearch size={20} />}
              placeholder="Search tests, results, and conditions"
              value={search}
              onChange={(event) => setSearch(event.currentTarget.value)}
            />
          </Container>
        </section>

        <Container className="education-index" size="xl" py={80}>
          <Group
            className="education-index-heading"
            justify="space-between"
            mb="xl"
            align="flex-end"
          >
            <Box>
              <Text className="section-label">Evidence library</Text>
              <Title order={2}>Articles for better conversations</Title>
            </Box>
            <Badge variant="light" size="lg">
              {visible.length} published
            </Badge>
          </Group>

          {state === "loading" ? <Loader /> : null}
          {state === "error" ? (
            <Alert color="red">The education library is temporarily unavailable.</Alert>
          ) : null}
          {state === "ready" && visible.length === 0 ? (
            <Paper className="empty-library" withBorder>
              <IconBook2 size={28} />
              <Title order={3}>No published article matches yet</Title>
              <Text c="dimmed">
                New pages are published only after clinical review.
              </Text>
            </Paper>
          ) : null}
          <SimpleGrid cols={{ base: 1, md: 2, lg: 3 }}>
            {visible.map((article) => (
              <Paper
                component="a"
                href={articleUrl(article.slug)}
                key={article.slug}
                className="education-card"
              >
                <Group justify="space-between">
                  <Badge variant="light">{article.category}</Badge>
                  {article.moduleKey ? (
                    <Text size="xs" fw={700}>{article.moduleKey}</Text>
                  ) : null}
                </Group>
                <Title order={3}>{article.title}</Title>
                <Text c="dimmed">{article.summary}</Text>
                <Group mt="auto" gap="xs" className="read-link">
                  Read the reviewed guide <IconArrowRight size={16} />
                </Group>
              </Paper>
            ))}
          </SimpleGrid>
        </Container>
      </main>
    </Box>
  );
}

export function EducationArticle({
  site,
  slug,
  style,
}: {
  site: PublicMarketingSiteDto;
  slug: string;
  style: CSSProperties;
}) {
  const [article, setArticle] = useState<PublicEducationArticleDto | null>(null);
  const [state, setState] = useState<LoadState>("loading");

  useEffect(() => {
    const controller = new AbortController();
    fetch(`/api/public/articles/${encodeURIComponent(slug)}`, {
      cache: "no-store",
      credentials: "omit",
      headers: { Accept: "application/json" },
      signal: controller.signal,
    })
      .then(async (response) => {
        if (response.status === 404) {
          setState("missing");
          return;
        }
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        setArticle(await response.json() as PublicEducationArticleDto);
        setState("ready");
      })
      .catch((error: unknown) => {
        if (error instanceof DOMException && error.name === "AbortError") return;
        setState("error");
      });
    return () => controller.abort();
  }, [slug]);

  useEffect(() => {
    if (!article) return;
    document.title = article.seoTitle;
    document.querySelector<HTMLMetaElement>('meta[name="description"]')
      ?.setAttribute("content", article.seoDescription);
  }, [article]);

  return (
    <Box className="site education-site" style={style}>
      <EducationHeader site={site} />
      <main>
        {state === "loading" ? (
          <Box className="status-page"><Loader /></Box>
        ) : null}
        {state === "missing" || state === "error" ? (
          <Container size="sm" py={100}>
            <Alert
              color={state === "missing" ? "blue" : "red"}
              title={
                state === "missing"
                  ? "This guide is awaiting clinical publication"
                  : "The guide could not be loaded"
              }
            >
              <Text>
                Education pages are shown only after a clinical reviewer has
                approved their content and references.
              </Text>
              <Button component="a" href="/learn" mt="md" variant="light">
                Browse published guides
              </Button>
            </Alert>
          </Container>
        ) : null}
        {article ? (
          <>
            <section className="article-masthead">
              <Container size="lg">
                <Button
                  component="a"
                  href="/learn"
                  variant="subtle"
                  leftSection={<IconArrowLeft size={16} />}
                  mb="xl"
                >
                  Education library
                </Button>
                <Group gap="sm">
                  <Badge variant="light">{article.category}</Badge>
                  {article.moduleKey ? <Badge variant="outline">{article.moduleKey}</Badge> : null}
                </Group>
                <Title order={1}>{article.title}</Title>
                <Text className="article-summary">{article.summary}</Text>
                <Group className="review-stamp">
                  <IconShieldCheck size={20} />
                  <Text size="sm" fw={700}>
                    Clinically reviewed · revision {article.revision} ·{" "}
                    {new Date(article.reviewedAt).toLocaleDateString()}
                  </Text>
                </Group>
              </Container>
            </section>

            <Container size="lg" className="article-layout">
              <article>
                <Alert color="blue" mb="xl" title="Use this guide with clinical advice">
                  This page provides general education and does not diagnose,
                  treat, or replace advice from a qualified healthcare
                  professional.
                </Alert>
                <RichArticleBody value={article.bodyMarkdown} />
              </article>
              <aside>
                <Paper className="evidence-panel">
                  <Text className="section-label">Evidence trail</Text>
                  <Title order={3}>References</Title>
                  <Stack gap="md" mt="lg">
                    {article.references.map((reference, index) => (
                      <Anchor
                        key={`${reference.url}-${index}`}
                        href={reference.url}
                        target="_blank"
                        rel="noreferrer"
                        className="reference-link"
                      >
                        <Text fw={700}>{reference.citationLabel}</Text>
                        <Text size="sm" c="dimmed">{reference.publisher}</Text>
                        <Group gap={4} mt={4}>
                          <Text size="xs">Open source</Text>
                          <IconExternalLink size={13} />
                        </Group>
                      </Anchor>
                    ))}
                  </Stack>
                </Paper>
              </aside>
            </Container>
          </>
        ) : null}
      </main>
    </Box>
  );
}
